/**
 * DropdownMenu — wrapper Radix UI avec styles Tailwind 4 V2.
 *
 * @remarks
 * Surface réutilisable (Settings, panels, headers, action menus). Pattern
 * compound : `<DropdownMenu>` + `<DropdownMenuTrigger asChild>` + `<DropdownMenuContent>`
 * + `<DropdownMenuItem>` + `<DropdownMenuSeparator>`.
 *
 * Toutes les classes utilisent les CSS vars Phase 8 (`text-*`, `gap-*`,
 * `transition-*`) — respectent automatiquement les sliders Settings.
 */

import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef } from 'react';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { cn } from '@/lib/utils/cn';

export const DropdownMenu = RadixDropdown.Root;
export const DropdownMenuTrigger = RadixDropdown.Trigger;
export const DropdownMenuPortal = RadixDropdown.Portal;
export const DropdownMenuSub = RadixDropdown.Sub;

// ── Content ────────────────────────────────────────────────────────────

type ContentProps = ComponentPropsWithoutRef<typeof RadixDropdown.Content>;

export const DropdownMenuContent = forwardRef<
  ComponentRef<typeof RadixDropdown.Content>,
  ContentProps
>(function DropdownMenuContent({ className, sideOffset = 4, ...props }, ref) {
  return (
    <RadixDropdown.Portal>
      <RadixDropdown.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'bg-surface border-border text-fg z-(--z-popover) min-w-[10rem] rounded-md border p-1 shadow-md',
          className,
        )}
        {...props}
      />
    </RadixDropdown.Portal>
  );
});

// ── Item ───────────────────────────────────────────────────────────────

type ItemProps = ComponentPropsWithoutRef<typeof RadixDropdown.Item>;

export const DropdownMenuItem = forwardRef<ComponentRef<typeof RadixDropdown.Item>, ItemProps>(
  function DropdownMenuItem({ className, ...props }, ref) {
    return (
      <RadixDropdown.Item
        ref={ref}
        className={cn(
          'focus:bg-bg-hover hover:bg-bg-hover flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
          className,
        )}
        {...props}
      />
    );
  },
);

// ── Separator ──────────────────────────────────────────────────────────

type SeparatorProps = ComponentPropsWithoutRef<typeof RadixDropdown.Separator>;

export const DropdownMenuSeparator = forwardRef<
  ComponentRef<typeof RadixDropdown.Separator>,
  SeparatorProps
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return (
    <RadixDropdown.Separator
      ref={ref}
      className={cn('bg-border my-1 h-px', className)}
      {...props}
    />
  );
});

// ── Label ──────────────────────────────────────────────────────────────

type LabelProps = ComponentPropsWithoutRef<typeof RadixDropdown.Label>;

export const DropdownMenuLabel = forwardRef<ComponentRef<typeof RadixDropdown.Label>, LabelProps>(
  function DropdownMenuLabel({ className, ...props }, ref) {
    return (
      <RadixDropdown.Label
        ref={ref}
        className={cn(
          'text-fg-muted text-2xs px-2 py-1 font-semibold tracking-widest uppercase',
          className,
        )}
        {...props}
      />
    );
  },
);

// ── SubTrigger ─────────────────────────────────────────────────────────

type SubTriggerProps = ComponentPropsWithoutRef<typeof RadixDropdown.SubTrigger>;

export const DropdownMenuSubTrigger = forwardRef<
  ComponentRef<typeof RadixDropdown.SubTrigger>,
  SubTriggerProps
>(function DropdownMenuSubTrigger({ className, ...props }, ref) {
  return (
    <RadixDropdown.SubTrigger
      ref={ref}
      className={cn(
        'focus:bg-bg-hover hover:bg-bg-hover data-[state=open]:bg-bg-hover flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    />
  );
});

// ── SubContent ─────────────────────────────────────────────────────────

type SubContentProps = ComponentPropsWithoutRef<typeof RadixDropdown.SubContent>;

export const DropdownMenuSubContent = forwardRef<
  ComponentRef<typeof RadixDropdown.SubContent>,
  SubContentProps
>(function DropdownMenuSubContent({ className, sideOffset = 4, ...props }, ref) {
  return (
    <RadixDropdown.Portal>
      <RadixDropdown.SubContent
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'bg-surface border-border text-fg z-(--z-popover) min-w-[10rem] rounded-md border p-1 shadow-md',
          className,
        )}
        {...props}
      />
    </RadixDropdown.Portal>
  );
});
